import { totalUnreadOf, type SearchResult } from '@swchat/client-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AvatarSrcProvider, Toast, useMediaQuery } from '../design';
import { useAnnouncementSummary, useAvatarLookup, useChannels, useClient, useConversations, usePrefs, useProjects } from '../lib/hooks';
import { needsAttention, PinnedChannelsBar } from './PinnedChannelsBar';
import { APP_TITLE, canAskPermission, incomingNotification, unreadTitle, type NotifyPermission } from '../lib/notify';
import { enableWebPush, onOpenConversation, showViaServiceWorker } from '../lib/pwa';
import { ConversationList, type ConversationFilter } from './ConversationList';
import { ConversationView } from './ConversationView';
import {
  LazyAnnouncements,
  LazyBotsDialog,
  LazyCallHost,
  LazyDevicesDialog,
  LazyOrganizeDialog,
  LazySettingsDialog,
  LazyStorageScreen,
  LazyUserPanel,
} from './lazy';
import { AllGroupsDialog } from './dialogs/AllGroupsDialog';
import { NewChatDialog } from './dialogs/NewChatDialog';
import { ComingSoon, NavRail, type NavSection } from './NavRail';
import { SearchPalette } from './SearchPalette';
import { playPing } from '../lib/sound';
import { readTheme, THEME_EVENT, toggleTheme } from './theme';
import './workspace.css';
import './organize.css';

/** Bố cục ba cột từ ≥ 1200px (khung ngữ cảnh là một cột, mở sẵn); hẹp hơn thì khung ngữ cảnh là ngăn kéo (W1.4). */
const DRAWER_QUERY = '(max-width: 1199px)';

/**
 * Giao diện chính "7w Chat" (W1.2): thanh điều hướng · danh sách hội thoại · hội thoại · khung ngữ cảnh. Chuyển
 * nguyên logic của screens/Main.tsx (chọn hội thoại, tìm kiếm D1, thông báo A7/Q4, số chưa đọc trên tiêu đề tab).
 * `embed`: chế độ nhúng trong 7w_portal — ẩn thanh điều hướng, bố cục gọn (W1.5).
 */
export function Workspace({
  initialConversation,
  onUnread,
  onOpenExternal,
  embed = false,
}: {
  initialConversation?: string;
  onUnread?: (count: number) => void;
  /** W2.5 (`/embed`): mở URL ngoài qua portal. */
  onOpenExternal?: (url: string) => void;
  embed?: boolean;
}) {
  const client = useClient();
  const conversations = useConversations();
  const avatarOf = useAvatarLookup(conversations);
  const [nav, setNav] = useState<NavSection>('chat');
  const [selected, setSelected] = useState<string | undefined>(initialConversation);
  /** Tin cần cuộn tới (kết quả tìm kiếm); `n` tăng mỗi lần bấm để mở lại cùng một tin. */
  const [focusMessage, setFocusMessage] = useState<{ conversationId: string; id: string; n: number }>();
  const focusSeq = useRef(0);
  const [dialog, setDialog] = useState<'new' | 'allgroups' | 'devices' | 'search' | 'bots' | 'settings' | 'organize' | undefined>();
  const [organizeTab, setOrganizeTab] = useState<'labels' | 'groups'>('labels');
  const [userPanel, setUserPanel] = useState(false);
  const prefs = usePrefs();
  const announce = useAnnouncementSummary();
  const channelState = useChannels();
  /** Kênh được mở thẳng từ thanh ghim (id) — màn Thông báo chọn kênh này khi mở. */
  const [announceChannel, setAnnounceChannel] = useState<string>();
  const [linkRequest, setLinkRequest] = useState(false);
  const [toast, setToast] = useState<string>();
  const [query, setQuery] = useState('');
  const [onlyCurrent, setOnlyCurrent] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [permission, setPermission] = useState<NotifyPermission>(() => ('Notification' in window ? Notification.permission : 'unsupported'));
  const projects = useProjects();
  /** E2: lọc danh sách hội thoại theo dự án ('' = tất cả). */
  const [projectFilter, setProjectFilter] = useState('');
  const [filter, setFilter] = useState<ConversationFilter>('all');
  /** < 1200px: khung ngữ cảnh dạng ngăn kéo — đóng sẵn, mở bằng nút trên đầu hội thoại; Esc đóng. */
  const drawer = useMediaQuery(DRAWER_QUERY);
  const [contextOpen, setContextOpen] = useState(() => !embed && !drawer);
  // Đổi qua lại ngưỡng 1200px: bố cục rộng mở sẵn khung ngữ cảnh, bố cục hẹp đóng ngăn kéo (không che hội thoại).
  const wasDrawer = useRef(drawer);
  useEffect(() => {
    if (wasDrawer.current === drawer) return;
    wasDrawer.current = drawer;
    setContextOpen(!embed && !drawer);
  }, [drawer, embed]);
  const [dark, setDark] = useState(() => readTheme() === 'dark');

  // Chế độ màu là cài đặt riêng của thiết bị này; nút ở thanh điều hướng và hộp thoại cài đặt cùng phát THEME_EVENT.
  useEffect(() => {
    const sync = () => setDark(readTheme() === 'dark');
    window.addEventListener(THEME_EVENT, sync);
    return () => window.removeEventListener(THEME_EVENT, sync);
  }, []);
  const toggleDark = useCallback(() => void toggleTheme(), []);

  const open = useCallback((id: string) => {
    setNav('chat');
    setSelected(id);
  }, []);

  useEffect(
    () =>
      client.on('link_request', () => {
        setLinkRequest(true);
        setToast('Có thiết bị mới đang chờ liên kết');
      }),
    [client],
  );
  useEffect(() => {
    if (initialConversation) open(initialConversation);
  }, [initialConversation, open]);

  // Q4: web chính (không nhúng) đã có quyền thông báo → đồng bộ subscription Web Push với server mỗi lần mở app
  // (thiết bị mới đăng nhập, trình duyệt xoay subscription, server đổi khóa VAPID). Bấm thông báo → mở hội thoại.
  const topLevel = window.self === window.top && !window.location.pathname.startsWith('/embed');
  useEffect(() => {
    if (!topLevel || permission !== 'granted') return;
    enableWebPush(client).catch((e: unknown) => console.warn('Không bật được thông báo đẩy', e));
  }, [client, topLevel, permission]);
  useEffect(() => (topLevel ? onOpenConversation(open) : undefined), [topLevel, open]);

  const current = conversations.find((c) => c.id === selected);
  // Hội thoại đã rời không tính vào tổng chưa đọc (tiêu đề tab, `unread` gửi portal).
  const totalUnread = totalUnreadOf(conversations);

  // A7: số chưa đọc trên tiêu đề tab và gửi cho portal (chế độ nhúng).
  useEffect(() => {
    document.title = unreadTitle(totalUnread);
    onUnread?.(totalUnread);
  }, [totalUnread, onUnread]);
  useEffect(() => () => void (document.title = APP_TITLE), []);

  // A7: thông báo trình duyệt khi tab ẩn — nội dung là plaintext đã giải mã trên máy, không qua server.
  const settingsRef = useRef(prefs.settings);
  settingsRef.current = prefs.settings;
  const titles = useRef(new Map<string, string>());
  titles.current = new Map(conversations.map((c) => [c.id, c.title]));
  useEffect(
    () =>
      client.on('incoming', (conversationId, messages) => {
        const spec = incomingNotification({
          visibility: document.visibilityState,
          permission: 'Notification' in window ? Notification.permission : 'unsupported',
          conversationId,
          conversationTitle: titles.current.get(conversationId),
          senderName: messages.length ? client.getUser(messages.at(-1)!.sender_user_id)?.display_name : undefined,
          messages,
        });
        if (!spec) return;
        const st = settingsRef.current;
        if (st.sound) playPing();
        if (!st.desktopNotifications) return;
        if (!st.notificationPreview) spec.body = 'Tin nhắn mới';
        let n: Notification;
        try {
          // Một số trình duyệt (Chrome Android, ngữ cảnh không bảo mật) ném lỗi với constructor Notification.
          n = new Notification(spec.title, { body: spec.body, tag: spec.tag });
        } catch {
          // Backlog #8: hiện qua service worker (bấm vào → service worker mở đúng hội thoại).
          showViaServiceWorker(spec.title, { body: spec.body, tag: spec.tag, conversationId }).catch(() => undefined);
          return;
        }
        n.onclick = () => {
          window.focus();
          open(conversationId);
          n.close();
        };
      }),
    [client, open],
  );

  // D1: tìm trong store cục bộ (đã giải mã), không gửi gì lên server.
  useEffect(() => {
    const q = query.trim();
    if (!q) return setResults([]);
    let cancelled = false;
    const timer = setTimeout(() => {
      void client.search(q, { conversationId: onlyCurrent ? selected : undefined, limit: 50 }).then((r) => !cancelled && setResults(r));
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [client, query, onlyCurrent, selected]);

  function openResult(r: SearchResult) {
    setDialog(undefined);
    open(r.conversation_id);
    setFocusMessage({ conversationId: r.conversation_id, id: r.message_id, n: ++focusSeq.current });
  }

  // W1.6: Ctrl+K / ⌘K mở bảng tìm kiếm toàn cục ở mọi nơi (cả khi đang gõ trong ô soạn); không chồng lên hộp thoại khác.
  const dialogRef = useRef(dialog);
  dialogRef.current = dialog;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'k' || !(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      e.preventDefault();
      // Mọi hộp thoại đang mở (của Workspace hay bên trong hội thoại: quản lý nhóm, chuyển tiếp…) đều là aria-modal.
      if (dialogRef.current || document.querySelector('[aria-modal="true"]')) return;
      setDialog('search');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const openOrganize = useCallback((tab: 'labels' | 'groups') => {
    setOrganizeTab(tab);
    setDialog('organize');
  }, []);

  /** Bố cục một cột: "Quay lại" về danh sách, focus trở lại mục hội thoại vừa xem (bàn phím không bị lạc). */
  function backToList() {
    const id = selected;
    setSelected(undefined);
    requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-testid="conv-item"][data-conv-id="${id}"]`)?.focus());
  }

  return (
    <AvatarSrcProvider value={avatarOf}>
    <div
      className={['ws-app', embed ? 'ws-embed' : '', prefs.settings.density === 'compact' ? 'density-compact' : '', current ? 'has-selection' : '', contextOpen ? 'has-context' : ''].filter(Boolean).join(' ')}
      data-testid="workspace"
    >
      {!embed && (
        <NavRail
          active={nav}
          unread={totalUnread}
          announceUnread={announce.unread}
          announceRing={channelState.channels.some(needsAttention)}
          userName={client.displayName ?? ''}
          userId={client.userId}
          dark={dark}
          onSelect={setNav}
          onToggleTheme={() => toggleDark()}
          onDevices={() => setDialog('devices')}
          onBots={() => setDialog('bots')}
          userPanelOpen={userPanel}
          onToggleUserPanel={() => setUserPanel((v) => !v)}
          userPanel={
            userPanel && (
              <LazyUserPanel
                onClose={() => setUserPanel(false)}
                onSettings={() => setDialog('settings')}
                onOrganize={() => openOrganize('labels')}
                onDevices={() => setDialog('devices')}
                onBots={() => setDialog('bots')}
                onLogout={() => void client.logout()}
              />
            )
          }
        />
      )}
      {nav === 'chat' ? (
        <div className="ws-chat-area">
          <div className="ws-chat-row">
          <ConversationList
            conversations={conversations}
            selected={selected}
            projects={projects}
            filter={filter}
            projectFilter={projectFilter}
            showNotify={canAskPermission(permission, window.self === window.top)}
            linkRequest={linkRequest}
            embed={embed}
            dark={dark}
            onFilter={setFilter}
            onProjectFilter={setProjectFilter}
            onSelect={(id) => (setSelected(id), setFocusMessage(undefined))}
            onOpenSearch={() => setDialog('search')}
            onNewChat={() => setDialog('new')}
            onAllGroups={() => setDialog('allgroups')}
            // Q4: được cấp quyền → effect phía trên đăng ký Web Push (thông báo cả khi đã đóng tab).
            onEnableNotify={() => void Notification.requestPermission().then(setPermission)}
            onLinkRequest={() => (setDialog('devices'), setLinkRequest(false))}
            onDevices={() => setDialog('devices')}
            onToggleTheme={() => toggleDark()}
            onManage={openOrganize}
            onNotice={setToast}
          />
          {current ? (
            <ConversationView
              key={current.id}
              conversation={current}
              conversations={conversations}
              projects={projects}
              focusMessage={focusMessage?.conversationId === current.id ? focusMessage : undefined}
              contextOpen={contextOpen}
              drawer={drawer}
              onToggleContext={setContextOpen}
              onBack={backToList}
              onOpenExternal={onOpenExternal}
            />
          ) : (
            <section className="ws-main ws-main-empty">
              <div className="ws-empty-state">
                <h2>Chọn một cuộc trò chuyện</h2>
                <p className="ws-muted">Tin nhắn được mã hóa đầu cuối — chỉ thiết bị của thành viên đọc được.</p>
              </div>
            </section>
          )}
          </div>
          {!embed && (
            <PinnedChannelsBar
              channels={channelState.channels}
              onOpen={(id) => {
                setAnnounceChannel(id);
                setNav('announce');
              }}
            />
          )}
        </div>
      ) : nav === 'announce' ? (
        <LazyAnnouncements
          projects={projects}
          canSend={announce.canSend}
          channels={channelState.channels}
          canManage={channelState.canManage}
          initialChannel={announceChannel}
          onChannelsChanged={channelState.reload}
          onChanged={announce.reload}
          onBack={() => setNav('chat')}
        />
      ) : nav === 'storage' ? (
        <LazyStorageScreen />
      ) : (
        <ComingSoon section={nav} onBack={() => setNav('chat')} />
      )}

      {dialog === 'new' && (
        <NewChatDialog
          projects={projects}
          initialProject={projectFilter || undefined}
          onClose={() => setDialog(undefined)}
          onCreated={(id) => {
            setDialog(undefined);
            open(id);
          }}
        />
      )}
      {dialog === 'allgroups' && (
        <AllGroupsDialog
          onClose={() => setDialog(undefined)}
          onOpen={(id) => {
            setDialog(undefined);
            open(id);
          }}
        />
      )}
      {dialog === 'devices' && <LazyDevicesDialog onClose={() => setDialog(undefined)} />}
      {dialog === 'bots' && <LazyBotsDialog onClose={() => setDialog(undefined)} />}
      {dialog === 'settings' && (
        <LazySettingsDialog
          onClose={() => setDialog(undefined)}
          onOrganize={() => openOrganize('labels')}
          onDevices={() => setDialog('devices')}
          onLogout={() => void client.logout()}
        />
      )}
      {dialog === 'organize' && <LazyOrganizeDialog initialTab={organizeTab} onClose={() => setDialog(undefined)} />}
      {dialog === 'search' && (
        <SearchPalette
          conversations={conversations}
          current={current}
          query={query}
          onlyCurrent={onlyCurrent}
          results={results}
          onQuery={setQuery}
          onOnlyCurrent={setOnlyCurrent}
          onOpenConversation={(id) => {
            setDialog(undefined);
            open(id);
            setFocusMessage(undefined);
          }}
          onOpenResult={openResult}
          onClose={() => setDialog(undefined)}
        />
      )}
      {toast && <Toast message={toast} onDismiss={() => setToast(undefined)} />}
      <LazyCallHost />
    </div>
    </AvatarSrcProvider>
  );
}
